<script>
    import { redirect, params } from "@roxi/routify";
    import { TenantStore } from "$/stores/TenantStore";
    import { TokenStore } from "$/stores/TokenStore";
    import { GiftStore } from "$/stores/GiftStore";
    import { LoginService } from "$/services/LoginService";
    import LoadingMessage from "./_templates/LoadingMessage.svelte";
    import { ClusterStore } from "$/stores/ClusterStore";
    import { onMount } from "svelte";
    import { GlobalSearchStore } from "$/stores/GlobalSearchStore";
    import TopicGroup from "$/components/topicGroup/TopicGroup.svelte";
    import { RecentTopicsStore } from "$/stores/RecentTopicsStore";
    import promotionalTopicGroups from "$/data/promotionalTopicGroups.json";
    import { ServiceConsts } from "$/services/ServiceConsts";
    import QuestionnaireDialog from "../components/modals/QuestionnaireDialog.svelte";
    
    let url,
        tenantId,
        topicId,
        snippetId,
        commentId,
        notificationQueryParams,
        searchQuery,
        payment,
        inbox;
    let showQuestionnaireDialog = false;

    $: topicGroups = [
        {
            title: "Your Recent Topics",
            description:
                $RecentTopicsStore && $RecentTopicsStore.length
                    ? "Here is the list of yours most recently visited topics &#128526;"
                    : "No topics yet, click on <span class='font-bold underline'>the + icon</span> to create a new topic or <span class='font-bold underline'>explore some examples</span> bellow &#128513; ",
            groups: [
                {
                    title: "",
                    topics: $RecentTopicsStore ?? [],
                },
            ],
        },
        ...promotionalTopicGroups,
    ];

    $: {
        if ($params) {
            loadContext();
        }
    }

    async function loadContext() {
        // url hash looks like this:
        // - for tenant - /#/{tenantId}
        // - for topic - /#/{tenantId}/t/{topicId}
        // - for snippet - /#/{tenantId}/t/{topicId}/s/{snippetId}
        // - for comment - /#/{tenantId}/t/{topicId}/s/{snippetId}/c/{commentId}
        // - for search - /#?q={searchQuery} or /#/{tenantId}?q={searchQuery}
        // - for payment - /#/{tenantId}/payment

        // if url is just "https://cowrite.me/" redirect to dashboard
        if (window.location.href === ServiceConsts.SHARED_URL)
            $redirect(ServiceConsts.DASHBOARD_URL);

        // handling path variables
        url = new URL(
            window.location.hash.substring(1),
            window.location.origin,
        );
        tenantId = url.pathname.split("/")[1] ?? null;
        topicId = url.pathname.split("/")[3] ?? null;
        snippetId = url.pathname.split("/")[5] ?? null;
        commentId = url.pathname.split("/")[7] ?? null;
        notificationQueryParams = commentId
            ? `?type=COMMENT&id=${commentId}`
            : snippetId
              ? `?type=SNIPPET&id=${snippetId}`
              : "";
        payment = url.pathname.split("/")[2] === "payment" ? true : false;
        inbox = url.pathname.split("/")[2] === "inbox" ? true : false;

        // removes leading #, but then search works for #tags
        let queryParams = new URLSearchParams(
            window.location.hash.substring(
                window.location.hash.indexOf("?") + 1,
            ),
        );
        searchQuery = queryParams.get("q") ?? null;

        /**
         * NOTE: For future debuging
         * - When index URL is called tenant store is initialized here
         * - Every other URL initializes stores throuh Sidebar.svelte
         */

        // if tenant id is present refresh token so that tenant is present in
        // JWT. This is important for newly added users because they have access
        // to tenant but don't have role inside JWT
        if (tenantId) {
            const refreshedToken = await LoginService.refreshTokenLogin();
            await TokenStore.updateTokenStore(refreshedToken);
            await TenantStore.load(tenantId);
        }

        // if tenant still null await init
        if ($TenantStore === null) await TenantStore.init();

        // load clusters if not loaded
        if ($ClusterStore.clusters === null) await ClusterStore.init($TenantStore.active.id);

        // load gifts if not loaded
        if ($GiftStore === null) GiftStore.init();

        // load recent topics if not loaded
        if (!$RecentTopicsStore) await RecentTopicsStore.load();

        // if no recent topics are present load 4 newest topics from clusters, else if there are less than 4 load the rest from clusters
        RecentTopicsStore.populateTopicsFromClusters($ClusterStore.clusters);

        // if search query is present load search
        if (searchQuery) {
            GlobalSearchStore.openSearchModal();
            GlobalSearchStore.setSearchQuery(searchQuery);
            // NOTE: Searches only for snippets for now
            GlobalSearchStore.search(
                decodeURIComponent(searchQuery),
                [],
                $TenantStore?.active?.id,
            );
        }
    }

    onMount(() => {
        loadContext();
        showQuestionnaireDialog = !!!$TokenStore.user.questionnaireDone;
    });
</script>

{#if showQuestionnaireDialog}
    <QuestionnaireDialog />
{/if}

{#if $RecentTopicsStore === null}
    <LoadingMessage />
{:else if topicId}
    {$redirect(`/${topicId}${notificationQueryParams}`)}
{:else if payment}
    {$redirect(`/payment`)}
{:else if inbox}
    {$redirect(`/inbox`)}
{:else if $RecentTopicsStore}
    <TopicGroup {topicGroups} />
{/if}

<style>
    @import "../main.css";
</style>
